<template>
  <section
    id="book-appointment"
    class="appointment-booking-section"
  >
    <div class="section-container">
      <!-- Section Header -->
      <div class="section-header">
        <span class="section-badge outline">
          Appointments
        </span>
        <h2 class="section-title">
          Book an Appointment
        </h2>
        <p class="section-subtitle">
          Schedule a consultation with the Office of Student Affairs and Services.
          Choose between student booking (requires login) or walk-in request.
        </p>
      </div>

      <!-- Tab Toggle -->
      <div class="d-flex justify-content-center mb-4">
        <div
          class="btn-group"
          role="group"
        >
          <button
            type="button"
            class="btn btn-sm px-4"
            :class="activeTab === 'walkin' ? 'btn-primary' : 'btn-outline-secondary'"
            @click="activeTab = 'walkin'"
          >
            <i class="fas fa-walking me-1" />
            Walk-in Request
          </button>
          <button
            type="button"
            class="btn btn-sm px-4"
            :class="activeTab === 'student' ? 'btn-primary' : 'btn-outline-secondary'"
            @click="activeTab = 'student'"
          >
            <i class="fas fa-user-graduate me-1" />
            Student Booking
          </button>
        </div>
      </div>

      <!-- Walk-in Request Form -->
      <div
        v-if="activeTab === 'walkin'"
        class="booking-card bg-white rounded-4 p-4 shadow-sm mx-auto"
        style="max-width: 680px;"
      >
        <h6 class="fw-bold mb-3">
          <i class="fas fa-calendar-plus text-success me-2" />
          Walk-in Appointment Request
        </h6>
        <p
          class="small mb-3"
          style="color: #64748b;"
        >
          No account needed. Verify your email with OTP first, then complete your appointment request.
        </p>

        <form
          novalidate
          @submit.prevent="submitWalkinForm"
        >
          <div class="row g-3">
            <div class="col-12 col-md-6">
              <label class="form-label fw-bold small">
                Full Name <span class="text-danger">*</span>
              </label>
              <input
                v-model="walkinForm.full_name"
                type="text"
                class="form-control"
                placeholder="Enter your full name"
                :disabled="submitting.walkin || !otpState.verified"
              >
            </div>
            <div class="col-12 col-md-6">
              <label class="form-label fw-bold small">
                Email Address <span class="text-danger">*</span>
              </label>
              <input
                v-model="walkinForm.email"
                type="email"
                class="form-control"
                placeholder="your.email@example.com"
                :disabled="submitting.walkin"
              >
              <div class="d-flex gap-2 mt-2">
                <button
                  type="button"
                  class="btn btn-sm btn-outline-success"
                  :disabled="submitting.walkin || otpState.sending || otpState.cooldown > 0 || !walkinForm.email"
                  @click="sendWalkinOtp"
                >
                  <i :class="['fas me-1', otpState.sending ? 'fa-spinner fa-spin' : 'fa-paper-plane']" />
                  {{ otpState.sending ? 'Sending OTP...' : (otpState.sent ? 'Resend OTP' : 'Send OTP') }}
                </button>

                <small
                  v-if="otpState.cooldown > 0"
                  class="text-muted align-self-center"
                >
                  Resend in {{ otpState.cooldown }}s
                </small>
              </div>

              <div
                v-if="otpState.sent && !otpState.verified"
                class="input-group input-group-sm mt-2"
              >
                <input
                  v-model="otpState.code"
                  type="text"
                  maxlength="6"
                  class="form-control"
                  placeholder="Enter 6-digit OTP"
                  :disabled="submitting.walkin || otpState.verifying"
                >
                <button
                  type="button"
                  class="btn btn-success"
                  :disabled="submitting.walkin || otpState.verifying || otpState.code.length !== 6"
                  @click="verifyWalkinOtp"
                >
                  <i :class="['fas me-1', otpState.verifying ? 'fa-spinner fa-spin' : 'fa-check']" />
                  {{ otpState.verifying ? 'Verifying...' : 'Verify OTP' }}
                </button>
              </div>

              <small
                v-if="otpState.error"
                class="text-danger d-block mt-2"
              >
                <i class="fas fa-exclamation-circle me-1" />
                {{ otpState.error }}
              </small>

              <small
                v-if="otpState.success"
                class="text-success d-block mt-2"
              >
                <i class="fas fa-check-circle me-1" />
                {{ otpState.success }}
              </small>
            </div>
            <div class="col-12 col-md-6">
              <label class="form-label fw-bold small">
                Contact Number <span class="text-danger">*</span>
              </label>
              <input
                v-model="walkinForm.contact_number"
                type="tel"
                class="form-control"
                placeholder="+63 912 345 6789"
                :disabled="submitting.walkin || !otpState.verified"
              >
            </div>
            <div class="col-12 col-md-6">
              <label class="form-label fw-bold small">
                Student ID (Optional)
              </label>
              <input
                v-model="walkinForm.student_id_number"
                type="text"
                class="form-control"
                placeholder="e.g., 2024-00001"
                :disabled="submitting.walkin || !otpState.verified"
              >
            </div>
            <div class="col-12 col-md-6">
              <label class="form-label fw-bold small">
                Available Slot <span class="text-danger">*</span>
              </label>
              <!-- Selected Slot Display / Select Button -->
              <div
                v-if="selectedSlotInfo"
                class="selected-slot-display d-flex align-items-center justify-content-between p-2 rounded border"
              >
                <div class="d-flex align-items-center">
                  <i class="fas fa-check-circle text-success me-2" />
                  <span>
                    <strong class="small">{{ selectedSlotInfo.date }}</strong>
                    <span class="text-muted mx-1">|</span>
                    <span class="small">{{ selectedSlotInfo.time }}</span>
                  </span>
                </div>
                <button
                  type="button"
                  class="btn btn-sm btn-outline-primary"
                  :disabled="submitting.walkin || !otpState.verified"
                  @click="openSlotCalendar"
                >
                  Change
                </button>
              </div>
              <button
                v-else
                type="button"
                class="btn btn-outline-primary w-100 py-2"
                :disabled="submitting.walkin || loadingSlots || !otpState.verified"
                @click="openSlotCalendar"
              >
                <i :class="['fas me-2', loadingSlots ? 'fa-spinner fa-spin' : 'fa-calendar-alt']" />
                {{ loadingSlots ? 'Loading...' : 'Select Date & Time' }}
              </button>
            </div>
            <div class="col-12 col-md-6">
              <label class="form-label fw-bold small">
                Concern Type <span class="text-danger">*</span>
              </label>
              <div class="concern-type-select-wrapper">
                <select
                  v-model="walkinForm.concern_type_id"
                  class="form-select concern-select"
                  :disabled="submitting.walkin || loadingConcernTypes || !otpState.verified"
                >
                  <option
                    :value="null"
                    disabled
                  >
                    {{ loadingConcernTypes ? 'Loading...' : '-- Select concern type --' }}
                  </option>
                  <option
                    v-for="ct in concernTypes"
                    :key="ct.id"
                    :value="ct.id"
                  >
                    {{ ct.name }}
                  </option>
                  <option value="other">
                    Other (please specify)
                  </option>
                </select>
                <i class="fas fa-chevron-down concern-select-icon" />
              </div>
            </div>
            <div
              v-if="walkinForm.concern_type_id === 'other'"
              class="col-12"
            >
              <label class="form-label fw-bold small">
                Please Specify <span class="text-danger">*</span>
              </label>
              <input
                v-model="walkinForm.other_concern"
                type="text"
                class="form-control"
                placeholder="Describe your concern..."
                maxlength="255"
                :disabled="submitting.walkin || !otpState.verified"
              >
            </div>
            <div class="col-12">
              <label class="form-label fw-bold small">
                Description <span class="text-danger">*</span>
              </label>
              <textarea
                v-model="walkinForm.description"
                class="form-control"
                rows="3"
                placeholder="Briefly describe the purpose of your appointment"
                :disabled="submitting.walkin || !otpState.verified"
              />
            </div>
          </div>

          <div
            v-if="walkinError"
            class="alert alert-danger py-2 small mt-3"
          >
            <i class="fas fa-exclamation-circle me-1" />
            {{ walkinError }}
          </div>

          <div
            v-if="walkinSuccess"
            class="alert alert-success py-2 small mt-3"
          >
            <i class="fas fa-check-circle me-1" />
            {{ walkinSuccess }}
          </div>

          <button
            type="submit"
            class="btn w-100 py-2 fw-bold mt-3"
            :disabled="submitting.walkin || !otpState.verified"
            style="background: linear-gradient(135deg, #10b981, #059669); color: white; border: none; border-radius: 10px;"
          >
            <i :class="['fas me-1', submitting.walkin ? 'fa-spinner fa-spin' : 'fa-calendar-check']" />
            {{ submitting.walkin ? 'Submitting...' : (otpState.verified ? 'Request Appointment' : 'Verify Email to Continue') }}
          </button>
        </form>
      </div>

      <!-- Student Booking -->
      <div
        v-if="activeTab === 'student'"
        class="booking-card bg-white rounded-4 p-4 shadow-sm mx-auto text-center"
        style="max-width: 500px;"
      >
        <div
          class="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
          style="width: 60px; height: 60px; background: linear-gradient(135deg, rgba(59,130,246,0.15), rgba(37,99,235,0.15)); color: #2563eb;"
        >
          <i
            class="fas fa-user-graduate"
            style="font-size: 1.5rem;"
          />
        </div>
        <h5
          class="fw-bold"
          style="color: #1e293b;"
        >
          Student Appointment Booking
        </h5>
        <p
          class="small mb-4"
          style="color: #64748b;"
        >
          Log in to your student account to book appointments with pre-filled information and track your appointment history.
        </p>
        <template v-if="isStudentLoggedIn">
          <p
            class="small fw-bold"
            style="color: #059669;"
          >
            <i class="fas fa-check-circle me-1" />
            You are logged in as {{ currentUser.name }}
          </p>
          <button
            class="btn px-4 py-2 fw-bold"
            style="background: linear-gradient(135deg, #3b82f6, #2563eb); color: white; border: none; border-radius: 10px;"
            @click="goToStudentAppointments"
          >
            <i class="fas fa-calendar-plus me-1" />
            Go to My Appointments
          </button>
        </template>
        <template v-else>
          <button
            class="btn px-4 py-2 fw-bold"
            style="background: linear-gradient(135deg, #10b981, #059669); color: white; border: none; border-radius: 10px;"
            @click="scrollToLogin"
          >
            <i class="fas fa-sign-in-alt me-1" />
            Sign In to Book
          </button>
        </template>
      </div>
    </div>

    <!-- Slot Calendar Modal -->
    <div
      v-if="showSlotCalendar"
      class="slot-modal-overlay"
      @click.self="showSlotCalendar = false"
    >
      <div class="slot-modal">
        <div class="slot-modal-header">
          <h5 class="mb-0">
            <i class="fas fa-calendar-alt text-primary me-2" />
            Select Appointment Date & Time
          </h5>
          <button
            type="button"
            class="btn-close"
            @click="showSlotCalendar = false"
          />
        </div>
        <div class="slot-modal-body">
          <div
            v-if="loadingSlots"
            class="text-center py-5"
          >
            <i class="fas fa-spinner fa-spin fa-2x text-primary mb-2" />
            <p class="text-muted">
              Loading slots...
            </p>
          </div>

          <div
            v-else-if="availableSlots.length === 0"
            class="text-center py-5"
          >
            <i
              class="fas fa-calendar-times fa-3x mb-3"
              style="color: #cbd5e1;"
            />
            <p class="text-muted">
              No available slots at the moment
            </p>
          </div>

          <div v-else>
            <SlotCalendar
              :slots="availableSlots"
              :selected-slot-id="walkinForm.slot_id"
              :selectable="true"
              :show-slots-inline="true"
              :initial-date="new Date()"
              @date-select="calendarSelectedDate = $event"
              @slot-select="selectSlotFromCalendar"
            />

            <div class="text-center py-3 text-muted">
              <i class="fas fa-hand-pointer me-1" />
              Select an available time slot directly inside a day cell.
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import '@/assets/css/landing/sections/common.css'
import { mapGetters } from 'vuex'
import LandingService from '@/services/shared/landing.service'
import SlotCalendar from '@/components/custom/SlotCalendar.vue'

export default {
  name: 'AppointmentBookingSection',

  components: { SlotCalendar },

  data () {
    return {
      activeTab: 'walkin',
      otpTimerId: null,
      loadingSlots: false,
      loadingConcernTypes: false,
      showSlotCalendar: false,
      calendarSelectedDate: null,
      availableSlots: [],
      concernTypes: [],
      walkinForm: {
        full_name: '',
        email: '',
        contact_number: '',
        student_id_number: '',
        slot_id: null,
        concern_type_id: null,
        other_concern: '',
        description: ''
      },
      otpState: {
        sending: false,
        sent: false,
        verifying: false,
        verified: false,
        code: '',
        verificationToken: '',
        cooldown: 0,
        success: '',
        error: ''
      },
      submitting: {
        walkin: false
      },
      walkinError: '',
      walkinSuccess: ''
    }
  },

  computed: {
    ...mapGetters({
      currentUser: 'auth/user',
      userType: 'auth/userType'
    }),

    isStudentLoggedIn () {
      return this.currentUser && this.userType === 'student'
    },

    selectedSlotInfo () {
      if (!this.walkinForm.slot_id) return null
      const slot = this.availableSlots.find(s => s.id === this.walkinForm.slot_id)
      if (!slot) return null
      return {
        date: this.formatSlotDate(slot.date),
        time: `${this.formatSlotTime(slot.start_time)} - ${this.formatSlotTime(slot.end_time)}`
      }
    }
  },

  watch: {
    'walkinForm.email' (newValue, oldValue) {
      if (newValue !== oldValue) {
        this.resetOtpState()
      }
    }
  },

  beforeDestroy () {
    this.clearOtpTimer()
  },

  mounted () {
    this.fetchSlots()
    this.fetchConcernTypes()
  },

  methods: {
    formatSlotDate (dateStr) {
      const d = new Date(dateStr + 'T00:00:00')
      return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
    },

    formatSlotTime (time) {
      if (!time) return ''
      const [hours, minutes] = time.substring(0, 5).split(':')
      const h = parseInt(hours, 10)
      const suffix = h >= 12 ? 'PM' : 'AM'
      const h12 = h % 12 || 12
      return `${h12}:${minutes} ${suffix}`
    },

    selectSlot (slot) {
      this.walkinForm.slot_id = slot.id
    },

    openSlotCalendar () {
      this.showSlotCalendar = true
      this.calendarSelectedDate = null
      if (this.availableSlots.length === 0) {
        this.fetchSlots()
      }
    },

    selectSlotFromCalendar (slot) {
      this.walkinForm.slot_id = slot.id
      this.showSlotCalendar = false
    },

    clearOtpTimer () {
      if (this.otpTimerId) {
        clearInterval(this.otpTimerId)
        this.otpTimerId = null
      }
    },

    startOtpCooldown (seconds = 60) {
      this.clearOtpTimer()
      this.otpState.cooldown = seconds

      this.otpTimerId = setInterval(() => {
        this.otpState.cooldown = Math.max(0, this.otpState.cooldown - 1)
        if (this.otpState.cooldown === 0) {
          this.clearOtpTimer()
        }
      }, 1000)
    },

    resetOtpState () {
      this.otpState.sent = false
      this.otpState.verifying = false
      this.otpState.verified = false
      this.otpState.code = ''
      this.otpState.verificationToken = ''
      this.otpState.cooldown = 0
      this.otpState.success = ''
      this.otpState.error = ''
      this.clearOtpTimer()
    },

    async sendWalkinOtp () {
      this.walkinError = ''
      this.walkinSuccess = ''
      this.otpState.error = ''
      this.otpState.success = ''

      const email = (this.walkinForm.email || '').trim().toLowerCase()
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(email)) {
        this.otpState.error = 'Please enter a valid email address before requesting OTP.'
        return
      }

      this.walkinForm.email = email
      this.otpState.sending = true

      try {
        const { data } = await LandingService.sendAppointmentOtp(email)
        this.otpState.sent = true
        this.otpState.verified = false
        this.otpState.code = ''
        this.otpState.verificationToken = ''
        this.otpState.success = data?.message || 'OTP sent. Please check your email.'
        this.startOtpCooldown(60)
      } catch (error) {
        this.otpState.error = error?.response?.data?.message || 'Failed to send OTP. Please try again.'
      } finally {
        this.otpState.sending = false
      }
    },

    async verifyWalkinOtp () {
      this.walkinError = ''
      this.walkinSuccess = ''
      this.otpState.error = ''
      this.otpState.success = ''

      const otp = (this.otpState.code || '').trim()
      if (!/^\d{6}$/.test(otp)) {
        this.otpState.error = 'Please enter the 6-digit OTP sent to your email.'
        return
      }

      this.otpState.verifying = true

      try {
        const { data } = await LandingService.verifyAppointmentOtp(this.walkinForm.email, otp)
        this.otpState.verified = true
        this.otpState.verificationToken = data?.data?.verification_token || ''
        this.otpState.success = data?.message || 'Email verified successfully.'
      } catch (error) {
        this.otpState.verified = false
        this.otpState.verificationToken = ''
        this.otpState.error = error?.response?.data?.message || 'Invalid OTP. Please try again.'
      } finally {
        this.otpState.verifying = false
      }
    },

    async fetchSlots () {
      this.loadingSlots = true
      try {
        const { data } = await LandingService.getAvailableSlots()
        this.availableSlots = (data.data || []).map(slot => ({
          ...slot,
          date: typeof slot.date === 'string' ? slot.date.slice(0, 10) : slot.date,
          is_available: slot.is_available ?? true,
          active_appointments_count: slot.active_appointments_count ?? 0
        }))
      } catch {
        this.availableSlots = []
      } finally {
        this.loadingSlots = false
      }
    },

    async fetchConcernTypes () {
      this.loadingConcernTypes = true
      try {
        const { data } = await LandingService.getConcernTypes()
        this.concernTypes = data.data || []
      } catch {
        this.concernTypes = []
      } finally {
        this.loadingConcernTypes = false
      }
    },

    async submitWalkinForm () {
      this.walkinError = ''
      this.walkinSuccess = ''

      // Basic validation
      const required = ['full_name', 'email', 'contact_number', 'slot_id', 'description']
      if (this.walkinForm.concern_type_id === 'other') {
        if (!this.walkinForm.other_concern || !this.walkinForm.other_concern.trim()) {
          this.walkinError = 'Please specify your concern.'
          return
        }
      } else {
        required.push('concern_type_id')
      }
      const missing = required.filter(field => !this.walkinForm[field])
      if (missing.length > 0) {
        this.walkinError = 'Please fill in all required fields.'
        return
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(this.walkinForm.email)) {
        this.walkinError = 'Please enter a valid email address.'
        return
      }

      if (!this.otpState.verified || !this.otpState.verificationToken) {
        this.walkinError = 'Please verify your email using OTP before submitting your appointment.'
        return
      }

      this.submitting.walkin = true

      try {
        const payload = { ...this.walkinForm }
        payload.otp_verification_token = this.otpState.verificationToken
        if (payload.concern_type_id === 'other') {
          payload.concern_type_id = null
          payload.description = payload.other_concern + (payload.description ? '\n' + payload.description : '')
        }
        delete payload.other_concern
        if (!payload.student_id_number) {
          delete payload.student_id_number
        }
        const { data } = await LandingService.submitAppointment(payload)
        const refNum = data.referenceNumber || ''
        this.walkinSuccess = `${data.message || 'Appointment submitted successfully!'}${refNum ? ` Reference: ${refNum}` : ''}`
        this.resetWalkinForm()
      } catch (error) {
        const msg = error?.response?.data?.message || error?.message?.message || 'Failed to submit. Please try again.'
        this.walkinError = msg
      } finally {
        this.submitting.walkin = false
      }
    },

    resetWalkinForm () {
      this.walkinForm = {
        full_name: '',
        email: '',
        contact_number: '',
        student_id_number: '',
        slot_id: null,
        concern_type_id: null,
        other_concern: '',
        description: ''
      }

      this.resetOtpState()
    },

    goToStudentAppointments () {
      this.$router.push({ name: 'student.appointments' })
    },

    scrollToLogin () {
      const el = document.getElementById('student-login')
      if (el) {
        const offset = 80
        const pos = el.getBoundingClientRect().top + window.pageYOffset - offset
        window.scrollTo({ top: pos, behavior: 'smooth' })
      }
    }
  }
}
</script>

<style scoped>
.appointment-booking-section {
  padding: clamp(2rem, 5vw, 4rem) 0;
  background: #fff;
}

.booking-card {
  border: 1px solid #e2e8f0;
}

.booking-card .form-control:focus,
.booking-card .form-select:focus {
  border-color: #10b981;
  box-shadow: 0 0 0 0.2rem rgba(16, 185, 129, 0.15);
}

.slot-selector {
  max-height: 280px;
  overflow-y: auto;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 8px;
  background: #f8fafc;
}

.slot-group {
  background: #fff;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 8px;
}

.slot-group:last-child {
  margin-bottom: 0;
}

.slot-date-header {
  font-weight: 600;
  font-size: 0.85rem;
  color: #1e293b;
  margin-bottom: 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid #e2e8f0;
}

.slot-options {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.slot-option {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  background: #f8fafc;
  border: 2px solid #e2e8f0;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.slot-option:hover {
  border-color: #10b981;
  background: #f0fdf4;
}

.slot-option--selected {
  border-color: #22c55e;
  background: #f0fdf4;
}

.slot-time {
  font-weight: 500;
  font-size: 0.9rem;
  color: #334155;
}

/* Selected Slot Display */
.selected-slot-display {
  background: #f0fdf4;
  border-color: #22c55e !important;
}

/* Concern Type Dropdown Styling */
.concern-type-select-wrapper {
  position: relative;
}
.concern-select {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  height: calc(2.25rem + 2px);
  padding-right: 2.25rem !important;
  padding-left: 0.75rem;
  background: #fff;
  border: 1px solid #ced4da;
  border-radius: 8px;
  font-size: 0.9rem;
  color: #334155;
  cursor: pointer;
  transition: all 0.15s ease;
}
.concern-select:hover {
  border-color: #10b981;
}
.concern-select:focus {
  border-color: #10b981;
  box-shadow: 0 0 0 0.2rem rgba(16, 185, 129, 0.15);
  outline: none;
}
.concern-select:disabled {
  background: #e9ecef;
  color: #6c757d;
  cursor: not-allowed;
}
.concern-select-icon {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #6b7280;
  font-size: 0.8rem;
  pointer-events: none;
}

/* Slot Modal */
.slot-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  padding: 16px;
}
.slot-modal {
  background: #fff;
  border-radius: 16px;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.2);
}
.slot-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e2e8f0;
}
.slot-modal-body {
  padding: 20px;
}

/* Calendar Grid */
.calendar-grid {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
}
.calendar-header-row {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}
.calendar-header-cell {
  padding: 8px 4px;
  text-align: center;
  color: #64748b;
  font-size: 0.75rem;
  font-weight: 600;
}
.calendar-body {
  background: #fff;
}
.calendar-row {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
}
.calendar-row:not(:last-child) {
  border-bottom: 1px solid #f1f5f9;
}
.calendar-cell {
  min-height: 44px;
  padding: 4px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  cursor: default;
}
.calendar-cell.other-month {
  background: #f8fafc;
}
.calendar-cell.other-month .day-number {
  color: #cbd5e1;
}
.calendar-cell.has-slots {
  cursor: pointer;
  background: #eff6ff;
}
.calendar-cell.has-slots:hover {
  background: #dbeafe;
}
.calendar-cell.selected-date {
  background: #3b82f6 !important;
}
.calendar-cell.selected-date .day-number {
  color: #fff;
}
.calendar-cell.selected-date .slots-badge {
  background: #fff;
  color: #3b82f6;
}
.day-number {
  font-weight: 600;
  font-size: 0.85rem;
  color: #334155;
}
.slots-badge {
  font-size: 0.6rem;
  background: #3b82f6;
  color: #fff;
  border-radius: 10px;
  padding: 1px 5px;
  margin-top: 1px;
}

/* Slot Options Grid */
.slot-options-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.slot-options-grid .slot-option {
  flex: 1 1 calc(50% - 4px);
  min-width: 140px;
}
</style>
